import { Badge } from "@/components/ui/badge";
import { LEVEL_BADGE_CLASSES } from "@/lib/level-badge";
import { droughtSupportingText, formatPctChange } from "@/lib/drought-detail";
import type { DroughtDetail } from "@shared/drought-detail";

function LabelledScore({ label, score, extra }: { label: string; score: 1 | 2 | 3 | 4 | undefined; extra?: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {score === undefined ? (
        <span className="text-xs text-muted-foreground">—</span>
      ) : (
        <Badge className={LEVEL_BADGE_CLASSES[score]}>{score}</Badge>
      )}
      {extra && <span className="text-xs text-muted-foreground">{extra}</span>}
    </span>
  );
}

/** The three drought scores (current, change, combined) plus the one
 * supporting sentence — methodology doc section 7.3. Display only. */
export function DroughtScores({ detail, isFuture }: { detail: DroughtDetail | undefined; isFuture: boolean }) {
  if (!detail) return null;

  const sentence = droughtSupportingText(detail, isFuture);
  const footnote = detail.worseningFlag === true ? "Worsening trend: the score is capped at 4." : detail.note;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <LabelledScore
          label="Current"
          score={detail.currentScore}
          extra={!isFuture && detail.climateClass ? `(${detail.climateClass})` : undefined}
        />
        {isFuture && (
          <>
            {detail.changeScore === undefined ? (
              <span className="text-xs text-muted-foreground">Change: not assessed</span>
            ) : (
              <LabelledScore label="Change" score={detail.changeScore} extra={formatPctChange(detail.pctChangeFrequency)} />
            )}
            <LabelledScore label="Combined" score={detail.combinedScore} />
          </>
        )}
      </div>
      {sentence && <p className="text-xs text-muted-foreground">{sentence}</p>}
      {footnote && <p className="text-xs text-muted-foreground">{footnote}</p>}
    </div>
  );
}
